import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import './FormNDIS.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const FormNDIS = () => {
  const [uploading, setUploading] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [formData, setFormData] = useState({
    // Client Personal Details
    salutation: '',
    firstName: '',
    preferredName: '',
    lastName: '',
    parentGuardianFirstName: '',
    parentGuardianLastName: '',
    sex: '',
    genderIdentity: '',
    pronouns: '',
    dateOfBirth: '',
    streetAddress: '',
    suburb: '',
    state: '',
    postcode: '',
    email: '',
    mobilePhone: '',
    homePhone: '',
    occupation: '',
    
    // NDIS Details
    supportCoordinator: '',
    ndisNumber: '',
    planStartDate: '',
    planEndDate: '',
    supportCategory: '',
    anticipatedHours: '',
    planManagementType: '',
    planManager: '',
    serviceStartDate: '',
    preferredDaysTimes: '',
    contactPerson: '',
    
    // GP Details
    gpName: '',
    practiceName: '',
    gpPhone: '',
    
    // Plan Goals
    ndisGoals: '',
    planGoalsFile: null,
    
    // Consent
    consentDate: '',
    signature: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      planGoalsFile: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setUploading(true);
    setUploadError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Use FormData for file upload
      const formDataToSend = new FormData();
      
      // Append all text fields
      Object.keys(formData).forEach(key => {
        if (key !== 'planGoalsFile' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append file if present
      if (formData.planGoalsFile) {
        formDataToSend.append('planGoalsFile', formData.planGoalsFile);
      }
      
      const response = await fetch(`${backendUrl}/api/ndis-referral/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
          setFormData({
            salutation: '',
            firstName: '',
            preferredName: '',
            lastName: '',
            parentGuardianFirstName: '',
            parentGuardianLastName: '',
            sex: '',
            genderIdentity: '',
            pronouns: '',
            dateOfBirth: '',
            streetAddress: '',
            suburb: '',
            state: '',
            postcode: '',
            email: '',
            mobilePhone: '',
            homePhone: '',
            occupation: '',
            supportCoordinator: '',
            ndisNumber: '',
            planStartDate: '',
            planEndDate: '',
            supportCategory: '',
            anticipatedHours: '',
            planManagementType: '',
            planManager: '',
            serviceStartDate: '',
            preferredDaysTimes: '',
            contactPerson: '',
            gpName: '',
            practiceName: '',
            gpPhone: '',
            ndisGoals: '',
            planGoalsFile: null,
            consentDate: '',
            signature: ''
          });
        }, 3000);
      } else {
        setUploadError(data.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      setUploadError('Network error. Please check your connection and try again.');
      console.error('Submit error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="form-ndis-standalone">
      <div className="form-container">
        <div className="form-header">
          <div className="form-branding">
            <img 
              src="/images/logos/clinical-minds-logo-white.png" 
              alt="Clinical Minds Psychology" 
              className="form-logo"
            />
          </div>
          <h1 className="form-title">NDIS Referral / Intake Form</h1>
          <p className="form-subtitle">Please complete all required fields below</p>
        </div>

        {uploadSuccess ? (
          <div className="upload-success">
            <CheckCircle size={64} className="success-icon" />
            <h3 className="success-title">Form Submitted Successfully!</h3>
            <p className="success-message">Thank you. We'll contact you within 24-48 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="ndis-form">
            {formsOff && <PreviewFormNotice />}
            {/* Client Personal Details */}
            <div className="form-section">
              <h2 className="section-title">Client Personal Details</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="salutation" className="form-label">Salutation</label>
                  <select
                    id="salutation"
                    name="salutation"
                    className="form-input"
                    value={formData.salutation}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                    <option value="Mx">Mx</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-input"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferredName" className="form-label">Preferred Name</label>
                  <input
                    type="text"
                    id="preferredName"
                    name="preferredName"
                    className="form-input"
                    value={formData.preferredName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="form-input"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="parentGuardianFirstName" className="form-label">Parent/Guardian First Name (if 16 or under)</label>
                  <input
                    type="text"
                    id="parentGuardianFirstName"
                    name="parentGuardianFirstName"
                    className="form-input"
                    value={formData.parentGuardianFirstName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="parentGuardianLastName" className="form-label">Parent/Guardian Surname (if 16 or under)</label>
                  <input
                    type="text"
                    id="parentGuardianLastName"
                    name="parentGuardianLastName"
                    className="form-input"
                    value={formData.parentGuardianLastName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="sex" className="form-label">Sex</label>
                  <select
                    id="sex"
                    name="sex"
                    className="form-input"
                    value={formData.sex}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Intersex">Intersex</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="genderIdentity" className="form-label">Gender Identity</label>
                  <input
                    type="text"
                    id="genderIdentity"
                    name="genderIdentity"
                    className="form-input"
                    value={formData.genderIdentity}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pronouns" className="form-label">Pronouns</label>
                  <input
                    type="text"
                    id="pronouns"
                    name="pronouns"
                    className="form-input"
                    value={formData.pronouns}
                    onChange={handleInputChange}
                    placeholder="e.g., he/him, she/her, they/them"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dateOfBirth" className="form-label">Date of Birth *</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    className="form-input"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="streetAddress" className="form-label">Street Address</label>
                <input
                  type="text"
                  id="streetAddress"
                  name="streetAddress"
                  className="form-input"
                  value={formData.streetAddress}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="suburb" className="form-label">Suburb</label>
                  <input
                    type="text"
                    id="suburb"
                    name="suburb"
                    className="form-input"
                    value={formData.suburb}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state" className="form-label">State</label>
                  <select
                    id="state"
                    name="state"
                    className="form-input"
                    value={formData.state}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="SA">SA</option>
                    <option value="WA">WA</option>
                    <option value="TAS">TAS</option>
                    <option value="NT">NT</option>
                    <option value="ACT">ACT</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="postcode" className="form-label">Postcode</label>
                  <input
                    type="text"
                    id="postcode"
                    name="postcode"
                    className="form-input"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    maxLength="4"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobilePhone" className="form-label">Mobile Phone</label>
                  <input
                    type="tel"
                    id="mobilePhone"
                    name="mobilePhone"
                    className="form-input"
                    value={formData.mobilePhone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="homePhone" className="form-label">Home Phone</label>
                  <input
                    type="tel"
                    id="homePhone"
                    name="homePhone"
                    className="form-input"
                    value={formData.homePhone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="occupation" className="form-label">Occupation</label>
                  <input
                    type="text"
                    id="occupation"
                    name="occupation"
                    className="form-input"
                    value={formData.occupation}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* NDIS Details */}
            <div className="form-section">
              <h2 className="section-title">NDIS Details</h2>
              <p className="section-description">If you are an NDIS Participant, please complete the following</p>

              <div className="form-group">
                <label htmlFor="supportCoordinator" className="form-label">Support Coordinator: Name, Phone and Email *</label>
                <textarea
                  id="supportCoordinator"
                  name="supportCoordinator"
                  className="form-textarea"
                  value={formData.supportCoordinator}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="ndisNumber" className="form-label">NDIS Number *</label>
                <input
                  type="text"
                  id="ndisNumber"
                  name="ndisNumber"
                  className="form-input"
                  value={formData.ndisNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="planStartDate" className="form-label">Plan Start Date *</label>
                  <input
                    type="date"
                    id="planStartDate"
                    name="planStartDate"
                    className="form-input"
                    value={formData.planStartDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="planEndDate" className="form-label">Plan End Date *</label>
                  <input
                    type="date"
                    id="planEndDate"
                    name="planEndDate"
                    className="form-input"
                    value={formData.planEndDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="supportCategory" className="form-label">Support Category</label>
                <input
                  type="text"
                  id="supportCategory"
                  name="supportCategory"
                  className="form-input"
                  value={formData.supportCategory}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="anticipatedHours" className="form-label">Approximate hours you anticipate to use with Psychology over next 12 months *</label>
                <input
                  type="text"
                  id="anticipatedHours"
                  name="anticipatedHours"
                  className="form-input"
                  value={formData.anticipatedHours}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="planManagementType" className="form-label">Plan Management Type *</label>
                <select
                  id="planManagementType"
                  name="planManagementType"
                  className="form-input"
                  value={formData.planManagementType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="Plan Managed">Plan Managed</option>
                  <option value="Self Managed">Self Managed</option>
                  <option value="Agency Managed">Agency Managed</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="planManager" className="form-label">If plan managed, who is the plan manager, and what email address should invoices be sent to?</label>
                <textarea
                  id="planManager"
                  name="planManager"
                  className="form-textarea"
                  value={formData.planManager}
                  onChange={handleInputChange}
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="serviceStartDate" className="form-label">When would you like to begin service? *</label>
                <input
                  type="text"
                  id="serviceStartDate"
                  name="serviceStartDate"
                  className="form-input"
                  value={formData.serviceStartDate}
                  onChange={handleInputChange}
                  placeholder="e.g., As soon as possible, January 2026"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="preferredDaysTimes" className="form-label">Do you have a preference of days or times for service? *</label>
                <textarea
                  id="preferredDaysTimes"
                  name="preferredDaysTimes"
                  className="form-textarea"
                  value={formData.preferredDaysTimes}
                  onChange={handleInputChange}
                  rows="2"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactPerson" className="form-label">After the referral is processed, who should we contact to schedule appointments and arrange a service agreement? *</label>
                <textarea
                  id="contactPerson"
                  name="contactPerson"
                  className="form-textarea"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  rows="2"
                  required
                />
              </div>
            </div>

            {/* GP Details */}
            <div className="form-section">
              <h2 className="section-title">Your GP Details</h2>

              <div className="form-group">
                <label htmlFor="gpName" className="form-label">Name of Doctor</label>
                <input
                  type="text"
                  id="gpName"
                  name="gpName"
                  className="form-input"
                  value={formData.gpName}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="practiceName" className="form-label">Practice Name</label>
                  <input
                    type="text"
                    id="practiceName"
                    name="practiceName"
                    className="form-input"
                    value={formData.practiceName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gpPhone" className="form-label">Phone Number (if known)</label>
                  <input
                    type="tel"
                    id="gpPhone"
                    name="gpPhone"
                    className="form-input"
                    value={formData.gpPhone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Plan Goals */}
            <div className="form-section">
              <h2 className="section-title">Plan Goals</h2>

              <div className="form-group">
                <label htmlFor="ndisGoals" className="form-label">NDIS Goals (if available please upload goals section of your plan)</label>
                <textarea
                  id="ndisGoals"
                  name="ndisGoals"
                  className="form-textarea"
                  value={formData.ndisGoals}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Please describe your NDIS goals..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="planGoalsFile" className="form-label">Upload Plan Goals Document (Optional)</label>
                <input
                  type="file"
                  id="planGoalsFile"
                  name="planGoalsFile"
                  className="form-input file-input"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <p className="form-help">Accepted formats: PDF, Word, or images (max 10MB)</p>
              </div>
            </div>

            {/* Consent */}
            <div className="form-section">
              <h2 className="section-title">Consent</h2>

              <div className="consent-text">
                <p>I consent for my practitioner to collect, store and utilise this personal information for the purposes of providing services to me in accordance with the relevant privacy legislation and any other legal requirements that may apply, as set out in our Privacy Policy.</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="signature" className="form-label">Full Name (as signature) *</label>
                  <input
                    type="text"
                    id="signature"
                    name="signature"
                    className="form-input"
                    value={formData.signature}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="consentDate" className="form-label">Date *</label>
                  <input
                    type="date"
                    id="consentDate"
                    name="consentDate"
                    className="form-input"
                    value={formData.consentDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            {uploadError && (
              <div className="upload-error">
                <p>{uploadError}</p>
              </div>
            )}

            <button 
              type="submit" 
              className="btn-submit"
              disabled={uploading || formsOff}
            >
              {uploading ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Submit Referral
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FormNDIS;
